import React from 'react';
import { Box, Typography, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { CCReport, CCReportComp, costPerDivision } from './construction-cost-report.type';
import { extractMetrics } from './construction-cost-report.util';
import CCReportBudgetInfo from './CCReportCostInfo';
import { Division } from '../glossary/glossary.type';
import { average, sortDivisions } from 'common/utils/common.util';
import { UNDEFINED_CHANGE_ORDER_DIVISION_CODE } from '../../common/constants/general';
import { ReportTypes } from '../../common/utils/util.type';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { Button } from 'components/index';

interface BudgetInfoProps {
  report: CCReport;
  displayProjections: boolean;
  divisions: Division[];
  costPerDivision: costPerDivision;
  typeOfReport: ReportTypes;
  includeSoftCost: boolean;
}

interface DivisionMetrics {
  divisionId: string;
  divisionName: string;
  divisionCode: string;
  divisionCostPercentage: number;
  metrics: any;
}

export const noDivision = {
  id: 'no division',
  code: 'N/A',
  name: 'No Division Assigned',
};

const CCReportCostPerDivision: React.FC<BudgetInfoProps> = ({
  report,
  displayProjections,
  divisions,
  costPerDivision,
  typeOfReport,
  includeSoftCost,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [showDivisionGraphs, setShowDivisionGraphs] = React.useState(false);
  const showToggleDivisionsGraphsControl = typeOfReport == ReportTypes.QCC && expanded;

  const handleChange = (isExpanded: boolean) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    event.preventDefault();
    setExpanded(isExpanded);
  };
  const toggleShowDivisionGraphs = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowDivisionGraphs(val => !val);
  };

  const division21Id = divisions.find(d => d.code === UNDEFINED_CHANGE_ORDER_DIVISION_CODE)?.id;
  const allDivisions = [...divisions.filter(d => d.code !== UNDEFINED_CHANGE_ORDER_DIVISION_CODE), noDivision];

  costPerDivision.comparableProjects?.forEach(costPerDivisionComp => {
    const localComp = report.comparableProjects?.find(comp => comp.comp.id === costPerDivisionComp.comp.id);
    const compIsIncludedLocally = localComp && localComp.isIncluded;
    costPerDivisionComp.isIncluded = compIsIncludedLocally ?? false;
  });

  const divisionData = aggregateDataByDivision(costPerDivision, allDivisions, includeSoftCost, division21Id);

  return (
    <Accordion elevation={2} sx={{ borderRadius: '10px !important' }} expanded={expanded} onChange={handleChange(!expanded)}>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': { justifyContent: 'space-between', alignItems: 'center' },
        }}
      >
        <Typography variant='h5' color='primary' sx={{ ml: 1, mb: 0 }}>
          Cost Per Division
        </Typography>
        {showToggleDivisionsGraphsControl && (
          <Button variant='text' onClick={toggleShowDivisionGraphs}>
            {showDivisionGraphs ? 'Hide' : 'Show'} Graphs
          </Button>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {divisionData.sort(sortDivisions).map(division => (
            <Box key={division.divisionId} sx={{ marginBottom: 5 }}>
              <Box mb={2} display='flex' justifyContent='center'>
                <Typography variant='h5'>
                  {division.divisionCode === 'N/A' ? division.divisionName : `${division.divisionCode} - ${division.divisionName}`}
                </Typography>
              </Box>
              <Divider></Divider>
              <CCReportBudgetInfo
                report={{ ...report, ...costPerDivision }}
                divisionId={division.divisionId}
                displayProjections={displayProjections}
                minBudgetPerGSF={division.metrics.minBudgetPerGSF}
                maxBudgetPerGSF={division.metrics.maxBudgetPerGSF}
                avgBudgetPerGSF={division.metrics.avgBudgetPerGSF}
                minBudgetPerNSF={division.metrics.minBudgetPerNSF}
                maxBudgetPerNSF={division.metrics.maxBudgetPerNSF}
                avgBudgetPerNSF={division.metrics.avgBudgetPerNSF}
                minBudgetPerUnit={division.metrics.minBudgetPerUnit}
                maxBudgetPerUnit={division.metrics.maxBudgetPerUnit}
                avgBudgetPerUnit={division.metrics.avgBudgetPerUnit}
                displayGraph={typeOfReport == ReportTypes.QCC && showDivisionGraphs && division.divisionId !== 'no division'}
                includeSoftCost={includeSoftCost}
                typeOfReport={ReportTypes.CC}
              />
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default CCReportCostPerDivision;

export const aggregateDataByDivision = (
  costPerDivision: costPerDivision,
  allDivisionIds: Division[],
  includeSoftCost: boolean,
  division21Id?: string,
) => {
  const divisionMetrics = new Map<string, DivisionMetrics>();
  const budgetField = includeSoftCost ? 'totalBudgetWithSoftCost' : 'totalBudgetWithoutSoftCost';

  allDivisionIds.forEach(division => {
    // Filter projects to include only those that have the specified division
    const divisionId = division.id === 'no division' ? null : division.id;
    const isNoDivision = divisionId === null;

    const getDivisionBudgetAmount = (comp: CCReportComp): number | undefined => {
      const base = comp.comp.divisionBudgets?.find(d => d.divisionId === divisionId)?.totalBudget;
      if (isNoDivision && division21Id) {
        const div21 = comp.comp.divisionBudgets?.find(d => d.divisionId === division21Id)?.totalBudget;
        if (base === undefined && div21 === undefined) return undefined;
        return (base ?? 0) + (div21 ?? 0);
      }
      return base;
    };

    const ratiosOfDivisionBudgetsToTotalBudgets =
      costPerDivision.comparableProjects
        ?.filter(comp => comp.isIncluded)
        .map(comp => {
          const divisionBudgetTotal = getDivisionBudgetAmount(comp) ?? 0;
          const totalBudget = comp.comp[budgetField] ?? 0;
          if (totalBudget === 0) return 0;
          return divisionBudgetTotal / totalBudget;
        }) ?? [];

    let divisionCostPercentage = average(ratiosOfDivisionBudgetsToTotalBudgets, item => item) * 100;
    divisionCostPercentage = parseFloat(divisionCostPercentage.toFixed(2));

    const filteredProjects = costPerDivision.comparableProjects?.reduce((acc, comp) => {
      const divisionBudgetAmount = getDivisionBudgetAmount(comp);
      if (division) {
        acc.push({
          ...comp,
          comp: {
            ...comp.comp,
            ...(includeSoftCost ? { totalBudgetWithSoftCost: divisionBudgetAmount } : { totalBudgetWithoutSoftCost: divisionBudgetAmount }),
          },
        });
      }
      return acc;
    }, [] as CCReportComp[]);

    const modifiedReport = {
      // ...costPerDivision,
      comparableProjects: filteredProjects,
    };

    const metrics = extractMetrics(modifiedReport, includeSoftCost);

    divisionMetrics.set(division.id, {
      divisionId: division.id,
      divisionCode: division.code,
      divisionName: division.name,
      divisionCostPercentage: divisionCostPercentage,
      metrics: metrics,
    });
  });

  return Array.from(divisionMetrics.values()).map(({ divisionId, divisionCode, divisionName, divisionCostPercentage, metrics }) => ({
    divisionId: divisionId,
    divisionCode: divisionCode,
    divisionName: divisionName,
    divisionCostPercentage: divisionCostPercentage,
    metrics: metrics,
  }));
};
